/** @jsx jsx */
import BackArrow from 'images/components/BackArrow.svg'
import React, {Dispatch, SetStateAction} from 'react'
import {jsx} from 'theme-ui'
import NextButton from './NextButton'
import PaymentFormPaypal from './PaymentFormPayPal'
import PaymentFormStripe from './PaymentFormStripe'
import {PaymentMethod} from './shared'
import {useTranslation} from 'react-i18next'
import checkedIcon from 'images/icons/checked.svg'
import circleIcon from 'images/icons/circle.svg'
import CreditCard from 'images/components/CreditCard.svg'
import PayPal from 'images/components/PayPal.svg'

export const paymentMethods: PaymentMethod[] = [
  {
    Icon: CreditCard,
    label: 'Card',
    methodName: 'stripe',
  },
  {
    Icon: PayPal,
    label: 'PayPal',
    methodName: 'payPal',
  },
]

interface IPaymentFormProps {
  hasAnsarSupport: boolean
  amount: number
  ansarAmount: number
  isMonthlyDonation: boolean
  donationPurpose: string
  handleBackClick: () => void
  isLoading: boolean
  payerName: string
  setPayerName: (payerName: string) => void
  paymentMethod: PaymentMethod['methodName']
  setPaymentMethod: (paymentMethod: PaymentMethod['methodName']) => void
  setStripeElementsComplete: Dispatch<
    SetStateAction<{
      cardNumber: boolean
      cardExpiry: boolean
      cardCvc: boolean
    }>
  >
  ansar: boolean
  setAnsar: (ansar: boolean) => void
}

function Option({checked}: {checked: boolean}) {
  return (
    <img
      sx={{
        height: 30,
        margin: 10,
      }}
      src={checked ? checkedIcon : circleIcon}
    />
  )
}

const PaymentForm: React.FC<IPaymentFormProps> = ({
  amount,
  ansarAmount,
  isMonthlyDonation,
  donationPurpose,
  handleBackClick,
  isLoading,
  setPayerName,
  payerName,
  setStripeElementsComplete,
  paymentMethod,
  setPaymentMethod,
  ansar,
  setAnsar,
  hasAnsarSupport,
}) => {
  const {t} = useTranslation()
  const totalAmount = amount + (ansar ? ansarAmount : 0)
  return (
    <div>
      <button
        type="button"
        onClick={handleBackClick}
        sx={{
          display: 'flex',
          alignItems: 'center',
          backgroundColor: 'transparent',
          border: 'none',
          color: '#A7A7A7',
          fontSize: 16,
          cursor: 'pointer',
        }}
      >
        <BackArrow sx={{mr: 12}} />
        {t('Back')}
      </button>
      <div
        sx={{
          color: 'primary',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'end',
        }}
      >
        <div>
          <h3
            sx={{
              fontSize: 16,
              fontWeight: 400,
              color: '#333',
              mb: 9,
            }}
          >
            {t('Donating')} ${amount}
            {isMonthlyDonation ? (
              <span sx={{color: 'primary', fontWeight: 'bold'}}>
                {' '}
                {t('per month')}
              </span>
            ) : (
              ''
            )}
          </h3>
          <div sx={{color: '#717A7B', fontSize: 14}}>
            {t('For')} {t(donationPurpose)}
          </div>
        </div>
        {hasAnsarSupport && (
          <div
            sx={{
              fontSize: 20,
              width: 80,
            }}
          >
            <div
              sx={{
                fontWeight: 200,
              }}
            >
              TOTAL
            </div>
            <div
              sx={{
                fontWeight: 700,
              }}
            >
              ${totalAmount}
            </div>
          </div>
        )}
      </div>
      {hasAnsarSupport && (
        <div
          sx={{
            mt: 12,
          }}
        >
          <div
            onClick={() => setAnsar(true)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              mt: 8,
              color: 'primary',
              cursor: 'pointer',
            }}
          >
            <Option checked={ansar} />
            <div>
              <div
                sx={{
                  fontWeight: 700,
                  fontSize: '16px',
                  marginLeft: '8px',
                }}
              >
                {t('Give an extra')} ${ansarAmount} {t('additionnels')}{' '}
              </div>
              <div
                sx={{
                  fontSize: '14px',
                  ml: '10px',
                }}
              >
                {t('to support the operating costs of UMMATY')}{' '}
                {amount >= 100 && t('(7% of your donation)')}
              </div>
            </div>
          </div>
          <div
            onClick={() => setAnsar(false)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              color: 'primary',
              cursor: 'pointer',
            }}
          >
            <Option checked={!ansar} />
            <div
              sx={{
                fontSize: 14,
                ml: '10px',
              }}
            >
              {t("I DON'T want to support the operating costs of UMMATY")}
            </div>
          </div>
        </div>
      )}
      <div
        sx={{
          mt: 24,
          backgroundColor: '#F5F5F5',
          borderRadius: 4,
        }}
      >
        <div
          sx={{
            width: '100%',
            display: 'flex',
          }}
        >
          {paymentMethods.map(({Icon, label, methodName}, index) => {
            const isSelected = paymentMethod === methodName
            const handleClick = () => {
              if (!isSelected) setPaymentMethod(methodName)
            }
            return (
              <button
                key={methodName}
                onClick={handleClick}
                type="button"
                sx={{
                  flex: 1,
                  fontSize: 14,
                  cursor: 'pointer',
                  height: 38,
                  borderTopRightRadius:
                    index === paymentMethods.length - 1 ? 4 : 0,
                  borderTopLeftRadius: index === 0 ? 4 : 0,
                  borderColor: 'primary',
                  borderLeftColor: index === 0 ? 'primary' : 'transparent',
                  borderWidth: 1,
                  backgroundColor: isSelected ? 'primary' : 'transparent',
                  color: isSelected ? '#fff' : 'primary',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <Icon
                  sx={{
                    mr: 6,
                    path: {
                      fill: isSelected ? 'white' : 'primary',
                    },
                  }}
                />
                {t(label)}
              </button>
            )
          })}
        </div>
        {paymentMethod == 'stripe' && (
          <PaymentFormStripe
            setPayerName={setPayerName}
            payerName={payerName}
            setStripeElementsComplete={setStripeElementsComplete}
          />
        )}
        {paymentMethod == 'payPal' && <PaymentFormPaypal />}
      </div>
      <NextButton type="submit" sx={{mt: 27}} disabled={isLoading} />
    </div>
  )
}

export default PaymentForm
